<template>
  <v-row>
    <v-col
      cols="12"
    >
      <v-card
        :loading="loading"
      >
        <v-card-title
          class="text-subtitle-2"
        >
          {{ $t(userId !== user.id ? 'passwords.request' : 'auth.change_password') }}
        </v-card-title>
        <v-divider />
        <v-card-text>
          <template v-if="userId !== user.id">
            <v-alert
              outlined
              type="warning"
              prominent
              :icon="false"
            >
              {{ $t('passwords.request_description') }}
            </v-alert>
            <v-card-actions>
              <v-spacer />
              <v-btn
                type="submit"
                color="primary"
                @click="requestReset()"
              >
                {{ $t('passwords.request') }}
              </v-btn>
            </v-card-actions>
          </template>
          <v-form
            v-else
            ref="form"
            @submit.prevent="changePassword()"
          >
            <v-row>
              <v-col
                cols="12"
              >
                <v-text-field
                  prepend-icon="lock"
                  name="password"
                  :label="$t('common.current_password')"
                  type="password"
                  :rules="rules.password"
                  validate-on-blur
                  :error-messages="errors.password"
                  v-model="password"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  prepend-icon="lock"
                  name="new_password"
                  :label="$t('common.new_password')"
                  type="password"
                  :rules="rules.new_password"
                  validate-on-blur
                  :error-messages="errors.new_password"
                  v-model="new_password"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  prepend-icon="lock"
                  name="new_password_confirmation"
                  :label="$t('common.password_confirmation')"
                  type="password"
                  v-model="new_password_confirmation"
                  @input="$refs.form.validate()"
                />
              </v-col>
            </v-row>
            <v-card-actions>
              <v-spacer />
              <v-btn
                type="submit"
                color="primary"
              >
                {{ $t('auth.change_password') }}
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import url from '../../../utils/url';
import {
  ruleRequired, ruleMinLength, ruleRegex, ruleConfirmed,
} from '../../../utils/rules';

export default {
  name: 'ChangePassword',
  props: {
    accountName: {
      type: String,
    },
    userId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      password: '',
      new_password: '',
      new_password_confirmation: '',
      errors: {
        password: [],
        new_password: [],
        new_password_confirmation: [],
      },
    };
  },
  computed: {
    ...mapGetters(['user']),
    profileUrl() {
      return url`/api/v1/accounts/${this.accountName}/users/${this.userId}`;
    },
    rules() {
      return {
        password: [
          ruleRequired(this.$t('validation.required', { attribute: this.$t('common.password') })),
        ],
        new_password: [
          ruleRequired(this.$t('validation.required', { attribute: this.$t('common.password') })),
          ruleMinLength(8, this.$t('validation.min.string', { attribute: this.$t('common.password'), min: 8 })),
          ruleRegex(/[A-Z]/, this.$t('validation.upper_char', { attribute: this.$t('common.password') })),
          ruleRegex(/[^A-Za-z0-9]/, this.$t('validation.special_char', { attribute: this.$t('common.password') })),
          ruleRegex(/.*[0-9].*/, this.$t('validation.number_required', { attribute: this.$t('common.password') })),
          ruleConfirmed(() => this.new_password_confirmation, this.$t('validation.confirmed', { attribute: this.$t('common.password') })),
        ],
      };
    },
  },
  methods: {
    ...mapActions(['success', 'error']),
    resetErrors() {
      Object.keys(this.errors).forEach(field => {
        this.errors[field] = [];
      });
    },
    async changePassword() {
      if (!this.$refs.form.validate()) return;

      try {
        this.loading = true;
        this.resetErrors();
        const {
          password,
          new_password,
          new_password_confirmation,
        } = this;
        const { status } = await this.http.post(this.profileUrl, {
          password,
          new_password,
          new_password_confirmation,
        });

        if (status === 200) {
          this.success(this.$t('auth.change_password_success'));
          return;
        }

        throw new Error('Error Changing Password!');
      } catch (e) {
        if (e.response && e.response.data && e.response.data.errors) {
          const { errors = {} } = e.response.data;
          Object.keys(this.errors).forEach(field => {
            this.errors[field] = errors[field] || [];
          });
          return;
        }
        this.error(e);
      } finally {
        this.loading = false;
      }
    },
    async requestReset() {
      try {
        this.resetErrors();
        const { data } = await this.http.get(this.profileUrl);
        const response = await this.http.post('/forgot-password', { email: data.user.email });

        if (response.status === 201) {
          this.success(this.$t('passwords.sent'));
          return;
        }

        throw new Error('Unknown Error Occurred!');
      } catch (e) {
        if (e.response && e.response.data && e.response.data.errors) {
          const { errors = {} } = e.response.data;
          const emailErrors = errors.email || [];
          this.error(new Error(emailErrors[0] || this.$t('common.unknown_error')));
          return;
        }

        this.error(new Error(this.$t('common.unknown_error')));
      }
    },
  },
};
</script>
